import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import Form, { ButtonItem, SimpleItem, EmptyItem, ColCountByScreen, Label, GroupItem } from 'devextreme-react/form'
import 'devextreme-react/tag-box'
import 'devextreme-react/tag-box'
import { useSnackbar } from 'notistack'
import { useDispatch, useSelector } from 'react-redux'
import { updateIndicator } from 'app/redux/store/features/configs'
import { updateClass } from 'app/redux/store/features/classes'
import formatCurrency from 'app/utils/components/formatCurrency/formatCurrency'
import { useMediaQuery } from '@mui/material'
import { termStartEndDate } from 'app/services/utils/components/termStartEndDate/termStartEndDate'
import { DropDownButtonRender } from 'app/utils/DropDownButtonRender/DropDownButtonRender'
import { SelectBox } from 'devextreme-react'
import { classDateEditorOptions } from './ClassDatesInfoHint'
import utilServices from 'app/services/util-services'
import termServices from 'app/services/pages/courses/setup/termServices/termServices'
import { validateClassDates } from './validateClassDates'

const UpdateClassForm = (props) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { handleSubmit } = useForm()
    const { loading, error } = useSelector((state) => state.classes)
    const { enqueueSnackbar } = useSnackbar()
    const formRef = useRef(null)
    const [apiCallInProgress, setApiCallInProgress] = useState(false)
    const [labelLocation, setLabelLocation] = useState('left')
    const sm = useMediaQuery('(max-width:600px)')
    const [termId, setTermId] = useState(props.classData.termId)
    const [locationId, setLocationId] = useState(props.classData.locationId)
    const [classTypeId, setClassTypeId] = useState(props.classData.classTypeId)
    const [classGenderTypeId, setClassGenderTypeId] = useState(props.classData.classGenderTypeId)
    const [ageGroupId, setAgeGroupId] = useState(props.classData.ageGroupId)
    const [prvClassId, setPrvClassId] = useState(props.classData.prvClassId)
    const [teacherId, setTeacherId] = useState(props.classData.teacherId)
    const [showValidationError, setShowValidationError] = useState(false)

    useEffect(() => {
        setLabelLocation(sm ? 'top' : 'left')
    }, [sm])

    useEffect(() => {
        setTermId(props.classData.termId)
        setLocationId(props.classData.locationId)
        setClassTypeId(props.classData.classTypeId)
        setClassGenderTypeId(props.classData.classGenderTypeId)
        setAgeGroupId(props.classData.ageGroupId)
        setPrvClassId(props.classData.prvClassId)
        setTeacherId(props.classData.teacherId)
    }, [props.classData])

    useEffect(() => {
        if (!apiCallInProgress || loading) return
        setApiCallInProgress(false)

        if (error) props.handleError(error)
        else {
            enqueueSnackbar('Operation completed successfully', {
                variant: 'success',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
            dispatch(updateIndicator({ inProgress: false }))
            navigate('/courses/setup/classes', { state: { navigateFrom: 'update-class' } })
        }
    }, [loading])

    const submitButtonOptions = {
        text: 'Save',
        type: 'default',
        width: 120,
        useSubmitBehavior: true,
    }

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'Normal',
        width: 120,
        onClick: () => navigate('/courses/setup/classes', { state: { navigateFrom: 'cancel-update-class' } }),
    }

    const validationRules = {
        className: [{ type: 'required', message: 'Class Name is required.' }],
        dateStart: [{ type: 'required', message: 'Overall Date Start is required.' }],
        dateEnd: [{ type: 'required', message: 'Overall Date End is required.' }],
        bookingStart: [{ type: 'required', message: 'Booking Start is required.' }],
        bookingEnd: [{ type: 'required', message: 'Booking End is required.' }],
        termStart: [{ type: 'required', message: 'Study Start is required.' }],
        termEnd: [{ type: 'required', message: 'Study Ends is required.' }],
        subTitle: [{ type: 'required', message: 'Sub Title is required.' }],
        termInfo: [{ type: 'required', message: 'Term Info is required.' }],
        shortDesc: [{ type: 'required', message: 'Age Info is required.' }],
        description: [{ type: 'required', message: 'Description is required.' }],
        description2: [{ type: 'required', message: 'Description 2 is required.' }],
    }

    const CurrencyEditorOptions = {
        format: (data) => formatCurrency(data),
    }

    const onTermValueChanged = useCallback((e) => {
        setTermId(e.value)
    }, [])

    const onLocationValueChanged = useCallback((e) => {
        setLocationId(e.value)
    }, [])

    const onClassTypeValueChanged = useCallback((e) => {
        setClassTypeId(e.value)
    }, [])

    const onGenderValueChanged = useCallback((e) => {
        setClassGenderTypeId(e.value)
    }, [])

    const onAgeGroupValueChanged = useCallback((e) => {
        setAgeGroupId(e.value)
    }, [])

    const onPrvClassValueChanged = useCallback((e) => {
        setPrvClassId(e.value)
    }, [])

    const onTeacherValueChanged = useCallback((e) => {
        setTeacherId(e.value)
    }, [])

    const prevtoFullNames = useMemo(() => {
        return (data) => {
            return data ? data.termYear + '/' + data.termNumber + ', ' + data.className : ''
        }
    }, [])

    const teacherFullName = useMemo(() => {
        return (data) => {
            return data ? data.firstName.toFullName(data.lastName) : ''
        }
    }, [])

    const onUpdateClass = useCallback(async () => {
        if (!termId || !classTypeId || !ageGroupId) {
            setShowValidationError(true)
            return
        }
        setShowValidationError(false)
        setApiCallInProgress(true)
        dispatch(updateIndicator({ inProgress: true }))

        const formInstance = formRef.current?.instance
        if (formInstance) {
            const validationResult = await formInstance.validate()
            const passed = validationResult !== false && validationResult?.isValid !== false
            if (!passed) {
                dispatch(updateIndicator({ inProgress: false }))
                setApiCallInProgress(false)
                return
            }
        }

        const formData = formInstance ? formInstance.option('formData') : {}
        const base = {
            ...props.classData,
            ...formData,
            termId,
            locationId,
            classTypeId,
            classGenderTypeId,
            ageGroupId,
            prvClassId,
            teacherId,
        }
        const additionalEditor = formInstance?.getEditor?.('additionalTeacherIds')
        if (additionalEditor) {
            const val = additionalEditor.option('value')
            base.additionalTeacherIds = utilServices.sanitizeAdditionalTeacherIds(
                base,
                Array.isArray(val) ? val : []
            )
        } else {
            base.additionalTeacherIds = utilServices.sanitizeAdditionalTeacherIds(
                base,
                Array.isArray(base.additionalTeacherIds) ? base.additionalTeacherIds : []
            )
        }

        try {
            const term = await termServices.getTermLookupById(base.termId)
            const termIdStr = String(base.termId ?? '')
            const termBoundsOk =
                term &&
                termIdStr &&
                String(term.id ?? '').toLowerCase() === termIdStr.toLowerCase()
            const dateMsg = validateClassDates({
                dateStart: base.dateStart,
                dateEnd: base.dateEnd,
                bookingStart: base.bookingStart,
                bookingEnd: base.bookingEnd,
                termStart: base.termStart,
                termEnd: base.termEnd,
                termDateStart: termBoundsOk ? term.dateStart : undefined,
                termDateEnd: termBoundsOk ? term.dateEnd : undefined,
            })
            if (dateMsg) {
                enqueueSnackbar(dateMsg, {
                    variant: 'error',
                    anchorOrigin: { vertical: 'top', horizontal: 'right' },
                })
                dispatch(updateIndicator({ inProgress: false }))
                setApiCallInProgress(false)
                return
            }
        } catch {
            enqueueSnackbar('Unable to validate dates against the term. Try again.', {
                variant: 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'right' },
            })
            dispatch(updateIndicator({ inProgress: false }))
            setApiCallInProgress(false)
            return
        }

        dispatch(
            updateClass({
                ...base,
                dateStart: utilServices.toUtcStartOfDayForApi(base.dateStart),
                dateEnd: utilServices.toUtcEndOfDayForApi(base.dateEnd),
                bookingStart: utilServices.toUtcStartOfDayForApi(base.bookingStart),
                bookingEnd: utilServices.toUtcEndOfDayForApi(base.bookingEnd),
                termStart: utilServices.toUtcStartOfDayForApi(base.termStart),
                termEnd: utilServices.toUtcEndOfDayForApi(base.termEnd),
            })
        )
    }, [
        props.classData,
        termId,
        locationId,
        classTypeId,
        classGenderTypeId,
        ageGroupId,
        prvClassId,
        teacherId,
        dispatch,
        enqueueSnackbar,
    ])

    return (
        <React.Fragment>
            <form onSubmit={handleSubmit(onUpdateClass)}>
                <Form
                    ref={formRef}
                    mode='form'
                    formData={props.classData}
                    showColonAfterLabel={false}
                    showValidationSummary={false}
                    labelLocation={labelLocation}>
                    <GroupItem>
                        <GroupItem>
                            <ColCountByScreen lg={2} md={2} sm={1} xs={1} />
                            <GroupItem caption='Details' cssClass='form-group-item'>
                                <GroupItem cssClass='form-group-item-margin'>
                                    <SimpleItem
                                        dataField='enableAutoSectionChat'
                                        editorType='dxCheckBox'
                                        colSpan={2}>
                                        <Label text='Class-wide chat (single auto section)' />
                                    </SimpleItem>
                                    <SimpleItem isRequired={true}>
                                        <Label text='Term' />
                                        <SelectBox
                                            dataSource={props.termLookupsStore}
                                            valueExpr='id'
                                            searchEnabled={true}
                                            showClearButton={true}
                                            defaultValue={props.classData.termId}
                                            value={termId}
                                            displayExpr={termStartEndDate}
                                            itemRender={termStartEndDate}
                                            onValueChanged={onTermValueChanged}
                                            dropDownButtonRender={DropDownButtonRender(
                                                props.selectBoxLoading.termLoading
                                            )}></SelectBox>
                                        {showValidationError && !termId && (
                                            <div
                                                className='dx-invalid-message'
                                                style={{ color: '#d9534f', fontSize: '12px', marginTop: '4px' }}>
                                                Term is required.
                                            </div>
                                        )}
                                    </SimpleItem>
                                    <SimpleItem>
                                        <Label text='Location Name' />
                                        <SelectBox
                                            dataSource={props.locationLookupStore}
                                            searchEnabled={true}
                                            showClearButton={true}
                                            defaultValue={props.classData.locationId}
                                            value={locationId}
                                            displayExpr='locationName'
                                            valueExpr='id'
                                            onValueChanged={onLocationValueChanged}
                                            dropDownButtonRender={DropDownButtonRender(
                                                props.selectBoxLoading.locationLoading
                                            )}></SelectBox>
                                    </SimpleItem>
                                    <SimpleItem dataField='className' validationRules={validationRules.className} />
                                    <SimpleItem isRequired={true}>
                                        <Label text='Class Type' />
                                        <SelectBox
                                            dataSource={props.classTypeLookupsStore}
                                            displayExpr='classTypeName'
                                            valueExpr='id'
                                            searchEnabled={true}
                                            showClearButton={true}
                                            defaultValue={props.classData.classTypeId}
                                            value={classTypeId}
                                            onValueChanged={onClassTypeValueChanged}
                                            dropDownButtonRender={DropDownButtonRender(
                                                props.selectBoxLoading.classTypeLoading
                                            )}></SelectBox>
                                        {showValidationError && !classTypeId && (
                                            <div
                                                className='dx-invalid-message'
                                                style={{ color: '#d9534f', fontSize: '12px', marginTop: '4px' }}>
                                                Class Type is required.
                                            </div>
                                        )}
                                    </SimpleItem>
                                    <SimpleItem>
                                        <Label text='Class Gender Type' />
                                        <SelectBox
                                            dataSource={props.genderTypeLookupStore}
                                            searchEnabled={true}
                                            showClearButton={true}
                                            defaultValue={props.classData.classGenderTypeId}
                                            value={classGenderTypeId}
                                            displayExpr='genderTypeName'
                                            valueExpr='id'
                                            onValueChanged={onGenderValueChanged}
                                            dropDownButtonRender={DropDownButtonRender(
                                                props.selectBoxLoading.classGenderTypeLoading
                                            )}></SelectBox>
                                    </SimpleItem>
                                    <SimpleItem dataField='subTitle' validationRules={validationRules.subTitle} />
                                    <SimpleItem isRequired={true}>
                                        <Label text='Age Group' />
                                        <SelectBox
                                            dataSource={props.ageGroupLookupStore}
                                            displayExpr='ageGroupName'
                                            valueExpr='id'
                                            searchEnabled={true}
                                            showClearButton={true}
                                            defaultValue={props.classData.ageGroupId}
                                            value={ageGroupId}
                                            onValueChanged={onAgeGroupValueChanged}
                                            dropDownButtonRender={DropDownButtonRender(
                                                props.selectBoxLoading.ageGroupLoading
                                            )}></SelectBox>
                                        {showValidationError && !ageGroupId && (
                                            <div
                                                className='dx-invalid-message'
                                                style={{ color: '#d9534f', fontSize: '12px', marginTop: '4px' }}>
                                                Age Group is required.
                                            </div>
                                        )}
                                    </SimpleItem>
                                    <SimpleItem dataField='validateMinimum' editorType='dxCheckBox' colSpan={2}>
                                        <Label text='Validate Minimum Age' />
                                    </SimpleItem>
                                    <SimpleItem dataField='validateMaximum' editorType='dxCheckBox' colSpan={2}>
                                        <Label text='Validate Maximum Age' />
                                    </SimpleItem>
                                    <SimpleItem dataField='shortDesc' validationRules={validationRules.shortDesc}>
                                        <Label text='Age Info' />
                                    </SimpleItem>
                                    <SimpleItem dataField='termInfo' validationRules={validationRules.termInfo}>
                                        <Label text='Term Info' />
                                    </SimpleItem>
                                    <SimpleItem
                                        dataField='classPrice'
                                        editorType='dxNumberBox'
                                        editorOptions={CurrencyEditorOptions}>
                                        <Label text='Class Price' />
                                    </SimpleItem>
                                    <SimpleItem
                                        dataField='feeChargeBasis'
                                        editorType='dxSelectBox'
                                        editorOptions={{
                                            items: [
                                                { value: 0, text: 'Per participant' },
                                                { value: 1, text: 'Flat fee (selected participants)' },
                                                { value: 2, text: 'Flat fee (all family members)' },
                                            ],
                                            valueExpr: 'value',
                                            displayExpr: 'text',
                                            searchEnabled: false,
                                        }}>
                                        <Label text='Fee charge basis' />
                                    </SimpleItem>
                                    <SimpleItem dataField='comments' editorType='dxTextArea' />
                                    <SimpleItem dataField='canDeposit' editorType='dxCheckBox'>
                                        <Label text='Deposit Enable' />
                                    </SimpleItem>
                                    <SimpleItem
                                        editorType='dxNumberBox'
                                        dataField='depositAmount'
                                        editorOptions={CurrencyEditorOptions}>
                                        <Label text='Deposit Amount' />
                                    </SimpleItem>
                                </GroupItem>
                            </GroupItem>
                            <GroupItem>
                                <GroupItem caption='Configuration' cssClass='form-group-item '>
                                    <GroupItem caption='Class dates' cssClass='form-group-item-margin'>
                                        <SimpleItem
                                            dataField='dateStart'
                                            editorType='dxDateBox'
                                            validationRules={validationRules.dateStart}
                                            editorOptions={classDateEditorOptions('dateStart')}>
                                            <Label text='Overall Date Start' />
                                        </SimpleItem>
                                        <SimpleItem
                                            dataField='dateEnd'
                                            editorType='dxDateBox'
                                            validationRules={validationRules.dateEnd}
                                            editorOptions={classDateEditorOptions('dateEnd')}>
                                            <Label text='Overall Date End' />
                                        </SimpleItem>
                                        <SimpleItem
                                            dataField='bookingStart'
                                            editorType='dxDateBox'
                                            validationRules={validationRules.bookingStart}
                                            editorOptions={classDateEditorOptions('bookingStart')}>
                                            <Label text='Booking Start' />
                                        </SimpleItem>
                                        <SimpleItem
                                            dataField='bookingEnd'
                                            editorType='dxDateBox'
                                            validationRules={validationRules.bookingEnd}
                                            editorOptions={classDateEditorOptions('bookingEnd')}>
                                            <Label text='Booking End' />
                                        </SimpleItem>
                                        <SimpleItem
                                            dataField='termStart'
                                            editorType='dxDateBox'
                                            validationRules={validationRules.termStart}
                                            editorOptions={classDateEditorOptions('termStart')}>
                                            <Label text='Study Start' />
                                        </SimpleItem>
                                        <SimpleItem
                                            dataField='termEnd'
                                            editorType='dxDateBox'
                                            validationRules={validationRules.termEnd}
                                            editorOptions={classDateEditorOptions('termEnd')}>
                                            <Label text='Study Ends' />
                                        </SimpleItem>
                                    </GroupItem>
                                    <GroupItem cssClass='form-group-item-margin'>
                                        <SimpleItem
                                            dataField='newEnrolDays'
                                            editorType='dxNumberBox'
                                            editorOptions={{ showSpinButtons: 'true' }}>
                                            <Label text='Delay New Enrol (Days after start date)' />
                                        </SimpleItem>
                                        <SimpleItem
                                            dataField='reEnrolDays'
                                            editorType='dxNumberBox'
                                            editorOptions={{ showSpinButtons: 'true' }}>
                                            <Label text='Open ReEnrol (Days before end date)' />
                                        </SimpleItem>
                                        <SimpleItem>
                                            <Label text='ReEnrol From Class (Last term)' />
                                            <SelectBox
                                                dataSource={props.classLookupsStore}
                                                searchEnabled={true}
                                                showClearButton={true}
                                                defaultValue={props.classData.prvClassId}
                                                value={prvClassId}
                                                displayExpr={prevtoFullNames}
                                                itemRender={prevtoFullNames}
                                                valueExpr='id'
                                                onValueChanged={onPrvClassValueChanged}
                                                dropDownButtonRender={DropDownButtonRender(
                                                    props.selectBoxLoading.prvClassIdLoading
                                                )}></SelectBox>
                                        </SimpleItem>
                                        <SimpleItem>
                                            <Label text='Teacher Name' />
                                            <SelectBox
                                                dataSource={props.teacherLookupsStorePrimary}
                                                valueExpr='id'
                                                searchEnabled={true}
                                                showClearButton={true}
                                                defaultValue={props.classData.teacherId}
                                                value={teacherId}
                                                displayExpr={teacherFullName}
                                                itemRender={teacherFullName}
                                                onValueChanged={onTeacherValueChanged}
                                                dropDownButtonRender={DropDownButtonRender(
                                                    props.selectBoxLoading.teacherLoading
                                                )}></SelectBox>
                                        </SimpleItem>
                                        <SimpleItem
                                            dataField='additionalTeacherIds'
                                            editorType='dxTagBox'
                                            editorOptions={{
                                                ...props.teacherEditorOptions,
                                                showSelectionControls: true,
                                                maxDisplayedTags: 3,
                                            }}>
                                            <Label text='Additional teachers' />
                                        </SimpleItem>
                                        <SimpleItem
                                            dataField='capacity'
                                            editorType='dxNumberBox'
                                            editorOptions={{ showSpinButtons: 'true' }}
                                        />
                                        <SimpleItem
                                            dataField='txnDueDays'
                                            editorType='dxNumberBox'
                                            editorOptions={{ showSpinButtons: 'true' }}>
                                            <Label text='Payment Due (Days)' />
                                        </SimpleItem>
                                        <SimpleItem dataField='registerCode'>
                                            <Label text='Register Code' />
                                        </SimpleItem>
                                        <SimpleItem
                                            dataField='description'
                                            editorType='dxTextArea'
                                            validationRules={validationRules.description}
                                        />
                                        <SimpleItem
                                            dataField='description2'
                                            editorType='dxTextArea'
                                            validationRules={validationRules.description2}
                                        />
                                    </GroupItem>
                                </GroupItem>
                            </GroupItem>
                        </GroupItem>
                    </GroupItem>
                    <EmptyItem />
                    <GroupItem>
                        <ColCountByScreen lg={2} md={2} sm={2} xs={2} />
                        <ButtonItem
                            buttonOptions={{
                                disabled: loading,
                                icon: loading ? 'refresh' : 'check',
                                ...submitButtonOptions,
                            }}
                            horizontalAlignment='right'
                        />
                        <ButtonItem buttonOptions={cancelButtonOptions} horizontalAlignment='left' />
                    </GroupItem>
                </Form>
            </form>
        </React.Fragment>
    )
}

export default UpdateClassForm
